import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box, Card, TextInput, PasswordInput, Button, Text, Alert, Stack,
} from '@mantine/core'
import { IconAlertCircle, IconCheck, IconPhone, IconLock } from '@tabler/icons-react'
import { authApi } from '@/api/auth'
import { BrandLogo } from '@/components/ui/BrandLogo'

const inputStyles = {
  label: { color: '#b6c2c9', fontWeight: 600, fontSize: 12, marginBottom: 4 },
  input: {
    backgroundColor: 'var(--ai-auth-input-bg)',
    borderColor: 'var(--ai-auth-border)',
    color: '#e9ecef',
    '&:focus': { borderColor: 'var(--ai-brand-verde)' },
  },
}

export function SetupPhone() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(true)

  useEffect(() => {
    if (!token) {
      setError('Link inválido. Solicite um novo via tela de reset TOTP.')
      setValidating(false)
      return
    }
    authApi.validateSetupPhoneToken(token)
      .then(res => setEmail(res.email))
      .catch(() => setError(
        'Link expirado ou inválido. Solicite um novo via tela de reset TOTP.',
      ))
      .finally(() => setValidating(false))
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!phone || !password) {
      setError('Preencha telefone e senha.')
      return
    }
    setError('')
    setLoading(true)
    try {
      await authApi.setupPhone(token, phone.trim(), password)
      setSuccess(true)
      setTimeout(() => navigate('/auth/forgot-totp', { replace: true }), 2500)
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      if (detail === 'invalid_credentials') {
        setError('Senha incorreta.')
      } else if (detail === 'invalid_phone') {
        setError('Telefone inválido. Use formato com DDI (ex: +5592999999999).')
      } else if (detail === 'token_invalid_or_expired') {
        setError('Link expirado. Solicite um novo via tela de reset TOTP.')
      } else {
        setError('Erro ao salvar telefone. Tente novamente em instantes.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--ai-auth-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <Card
        shadow="md"
        padding="xl"
        radius="md"
        style={{ width: '100%', maxWidth: 440, backgroundColor: 'var(--ai-auth-card-bg)', border: '1px solid var(--ai-auth-border)' }}
      >
        <Stack gap="md">
          <Box style={{ textAlign: 'center' }}>
            <BrandLogo variant="symbol" width={48} />
            <Text size="lg" fw={700} c="#e9ecef" mt="md">Cadastrar telefone</Text>
            {email && (
              <Text size="xs" c="dimmed" mt={4}>
                Conta: <strong style={{ color: '#b6c2c9' }}>{email}</strong>
              </Text>
            )}
          </Box>

          {validating && (
            <Text size="sm" c="dimmed" ta="center">Validando link...</Text>
          )}

          {!validating && error && !success && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
            >
              {error}
            </Alert>
          )}

          {!validating && success && (
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              Telefone cadastrado! Redirecionando para tela de reset TOTP...
            </Alert>
          )}

          {!validating && email && !success && (
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Telefone (com DDI)"
                  placeholder="+5592999999999"
                  value={phone}
                  onChange={e => setPhone(e.currentTarget.value)}
                  leftSection={<IconPhone size={14} />}
                  required
                  styles={inputStyles}
                />
                <PasswordInput
                  label="Sua senha atual"
                  placeholder="Senha do ConsoleAI"
                  value={password}
                  onChange={e => setPassword(e.currentTarget.value)}
                  leftSection={<IconLock size={14} />}
                  required
                  styles={inputStyles}
                />
                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  radius="sm"
                  loading={loading}
                  style={{
                    backgroundColor: 'var(--ai-brand-verde)',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  Salvar telefone
                </Button>
                <Button
                  type="button"
                  variant="subtle"
                  size="xs"
                  color="gray"
                  onClick={() => navigate('/login')}
                >
                  Cancelar
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </Card>
    </Box>
  )
}
